/**
 * Which lobby a request is for: the slug in the path, or DK's deep-linked id.
 *
 * DK loads every game from one host as `/<segment>?tenantId=<code>&gameId=<guid>` (DKPLAT-467).
 * That URL is configured on DK's side and cannot change, so on `/lobby` the `gameId` param
 * carries a LOBBY id rather than a pickem one — the param name is a fixed contract meaning
 * "the id of the thing this path shows", not a promise about which table it points at.
 *
 * What their proxy actually delivers to us is NOT `/lobby` bare. It echoes the tenant code back
 * as the path segment, so the app is entered at:
 *
 *   /tenant/dk/lobby/dk?tenantId=dk&gameId=<lobby guid>
 *
 * The app is mounted with basename `/tenant/dk`, so `:lobbySlug` reads as `dk` — and `dk` is the
 * real slug of DK's default lobby. A plain "path slug always wins" rule therefore resolves the
 * default lobby and silently drops the id, which is exactly the bug this fixes.
 *
 * Precedence:
 *   1. a guid-shaped `gameId`, when the path carries no slug OR carries only the tenant code
 *      echoed back by DK's proxy (neither is a request for a specific lobby)
 *   2. the path slug — so every existing `/lobby/<slug>` link resolves exactly as it does today,
 *      even with a stray `gameId` alongside it, which happens the moment a deep-linked visitor
 *      opens My Games and the query string is carried across
 *   3. a non-guid `gameId`, unchanged from before
 *
 * Requiring the guid shape at (1) is deliberate: a junk or stale `gameId` falls through to the
 * path slug, so `/tenant/dk/lobby/dk?gameId=<not-a-guid>` still shows the default lobby rather
 * than 404ing.
 */

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function resolveLobbyIdentifier(
  pathSlug: string | undefined,
  deepLinkId: string | null | undefined,
  tenantSlug?: string
): string | undefined {
  const slug = pathSlug?.trim();
  const id = deepLinkId?.trim();

  const tenant = tenantSlug?.trim().toLowerCase();
  const slugIsTenantEcho = !!slug && !!tenant && slug.toLowerCase() === tenant;

  if (id && UUID_REGEX.test(id) && (!slug || slugIsTenantEcho)) return id;
  if (slug) return slug;
  return id ? id : undefined;
}
